/**
 * /policies/payroll/gl-mapping — Session 3c PR 18.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { GlMappingItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollGlMappingPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-gl-mapping", le],
    queryFn: () => session3cApi.listGlMappings(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<GlMappingItem>[] = [
    {
      headerKey: "policies.payroll_gl.col.component",
      label: "Component",
      render: (r) => <span className="font-medium">{r.payroll_component}</span>,
    },
    {
      headerKey: "policies.payroll_gl.col.account",
      label: "Account",
      render: (r) => <span className="font-mono text-xs">{r.gl_account}</span>,
    },
    {
      headerKey: "policies.payroll_gl.col.name",
      label: "Account name",
      render: (r) => <span className="text-xs text-text-secondary">{r.gl_account_name}</span>,
    },
    {
      headerKey: "policies.payroll_gl.col.system",
      label: "System",
      render: (r) => <span className="text-xs uppercase">{r.system}</span>,
    },
  ];

  return (
    <PolicyListPage<GlMappingItem>
      title={t("policies.payroll_gl.title", "GL Mapping")}
      subtitle={t("policies.payroll_gl.subtitle", "Per-LE D365 · SAP · QuickBooks account mapping for payroll GL journal posting.")}
      agent="rawatib"
      domain="payroll_gl"
      newButtonLabel={t("policies.payroll_gl.cta.new", "New Mapping")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_gl.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_gl.suggestion.clean_body", "Maps payroll components to GL account codes. Used by /operations/payroll/gl-output journal generation."),
          primaryActionLabel: t("policies.payroll_gl.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-gl-mapping-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_gl.title", "GL Mapping"),
        window_label: t("policies.payroll_gl.window", "active mappings"),
      }}
    />
  );
}
